import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Home, Wrench, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { services, siteConfig } from '../data/mock';

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services', current: true }
];

const ServicesPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1F3B60' }}>
      <Helmet>
        <title>Services | Pacific Crest Properties — Los Angeles Real Estate</title>
        <meta name="description" content="Pacific Crest Properties offers two core services: fast, as-is property offers and professional renovation services for Los Angeles homeowners." />
        <meta name="keywords" content="Los Angeles property services, home renovation LA, sell house fast Los Angeles, as-is property buyers" />
        <link rel="canonical" href="https://pacificcrestprop.com/services" />
        <meta property="og:title" content="Services | Pacific Crest Properties" />
        <meta property="og:description" content="Fast property offers and professional renovation services for LA homeowners." />
        <meta property="og:url" content="https://pacificcrestprop.com/services" />
      </Helmet>
      
      <Header />
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} variant="dark" />
      </div>
      
      {/* Hero Section */}
      <section className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
            Our Services
          </span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Real Estate Solutions
          </h1>
          <div className="divider-gold mx-auto mb-8" />
          <p className="text-xl text-white/70 leading-relaxed max-w-3xl mx-auto">
            Pacific Crest Properties provides Los Angeles homeowners with two flexible paths: fast, as-is property offers and professional renovation services.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Property Offers Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                  <Home className="w-7 h-7" style={{ color: '#1F3B60' }} />
                </div>
                <h2 className="font-display font-bold text-2xl" style={{ color: '#1F3B60' }}>
                  {services.offers.title}
                </h2>
              </div>
              
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#1F3B60' }}>Sell Your Home Quickly in Los Angeles</h3>
              <p className="text-base mb-6" style={{ color: '#3A5A85' }}>As-Is Property Solutions for LA Homeowners</p>

              <p className="leading-relaxed mb-8" style={{ color: '#3A5A85' }}>
                {services.offers.description}
              </p>

              <ul className="space-y-3 mb-8">
                {services.offers.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: 'rgba(214, 169, 79, 0.15)' }}>
                      <Check className="w-3.5 h-3.5" style={{ color: '#D6A94F' }} />
                    </div>
                    <span style={{ color: '#3A5A85' }}>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={services.offers.cta.href}
                  className="btn-gold inline-flex items-center justify-center space-x-2 group"
                >
                  <span>{services.offers.cta.text}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/sell-your-home"
                  className="inline-flex items-center justify-center space-x-2 font-semibold px-6 py-3 rounded-full border-2 transition-all duration-300"
                  style={{ color: '#1F3B60', borderColor: '#1F3B60' }}
                >
                  <span>Learn More</span>
                </Link>
              </div>
            </div>

            {/* Renovation Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                  <Wrench className="w-7 h-7" style={{ color: '#1F3B60' }} />
                </div>
                <h2 className="font-display font-bold text-2xl" style={{ color: '#1F3B60' }}>
                  {services.renovation.title}
                </h2>
              </div>
              
              <h3 className="text-lg font-semibold mb-6" style={{ color: '#1F3B60' }}>Renovation Services for Los Angeles Homes</h3>

              <p className="leading-relaxed mb-8" style={{ color: '#3A5A85' }}>
                {services.renovation.description}
              </p>

              <ul className="space-y-3 mb-8">
                {services.renovation.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: 'rgba(214, 169, 79, 0.15)' }}>
                      <Check className="w-3.5 h-3.5" style={{ color: '#D6A94F' }} />
                    </div>
                    <span style={{ color: '#3A5A85' }}>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={services.renovation.cta.href}
                  className="btn-gold inline-flex items-center justify-center space-x-2 group"
                >
                  <span>{services.renovation.cta.text}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to={siteConfig.links.renovations}
                  className="inline-flex items-center justify-center space-x-2 font-semibold px-6 py-3 rounded-full border-2 transition-all duration-300"
                  style={{ color: '#1F3B60', borderColor: '#1F3B60' }}
                >
                  <span>Learn More</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 px-4 text-center">
        <Link
          to="/"
          className="inline-flex items-center space-x-2 font-medium text-white/60 hover:text-white transition-colors duration-300"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          <span>Back to Home</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
